import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone,
  PhoneOff,
  PhoneMissed,
  Clock,
  Search,
  User,
  FileText,
  Volume2,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Badge, LoadingSpinner, Button } from './components';
import { TranscriptDisplay } from './components';

function CallsPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedCall, setExpandedCall] = useState(null);
  const [recordingUrls, setRecordingUrls] = useState({});

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ordering: '-initiated_at',
        page: currentPage,
        page_size: 50,
      };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await apiService.getOutgoingCalls(params);
      setCalls(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch calls');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const loadRecording = async (callId) => {
    if (recordingUrls[callId]) return;
    try {
      const data = await apiService.getCallRecordingUrl(callId);
      setRecordingUrls(prev => ({ ...prev, [callId]: data.url }));
    } catch (err) {
      console.error('Failed to load recording:', err);
    }
  };

  const toggleExpand = async (call) => {
    if (expandedCall === call.id) {
      setExpandedCall(null);
      return;
    }
    setExpandedCall(call.id);
    if (call.recording_s3_key) {
      loadRecording(call.id);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <Phone className="w-3.5 h-3.5 text-green-600" />;
      case 'failed': case 'busy': return <PhoneOff className="w-3.5 h-3.5 text-red-600" />;
      case 'no_answer': return <PhoneMissed className="w-3.5 h-3.5 text-amber-600" />;
      default: return <Phone className="w-3.5 h-3.5 text-gray-400" />;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': case 'busy': return 'error';
      case 'no_answer': case 'cancelled': return 'warning';
      default: return 'neutral';
    }
  };

  const totalPages = Math.ceil(totalCount / 50);

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        <div className="mb-3">
          <h1 className="text-lg font-semibold text-gray-900">Calls</h1>
          <p className="text-xs text-gray-600 mt-0.5">{totalCount} outgoing calls</p>
        </div>

        {error && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <p className="text-xs text-red-800">{error}</p>
          </Panel>
        )}

        <Panel className="mb-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search by name or number..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-8 pr-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-red-800"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="no_answer">No Answer</option>
              <option value="busy">Busy</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </Panel>

        <Panel noPadding>
          {loading ? (
            <div className="p-6"><LoadingSpinner /></div>
          ) : calls.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-xs">No calls found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {calls.map((call) => (
                <div key={call.id}>
                  <div
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                    onClick={() => toggleExpand(call)}
                  >
                    {getStatusIcon(call.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-900">
                          {call.person_name || call.to_number}
                        </span>
                        {call.person_name && (
                          <span className="text-xs text-gray-400">{call.to_number}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-gray-500">{formatDate(call.initiated_at)}</span>
                        {call.initiated_by_name && (
                          <span className="text-xs text-gray-400">by {call.initiated_by_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {call.transcription_status === 'completed' && (
                        <FileText className="w-3.5 h-3.5 text-blue-500" title="Transcript available" />
                      )}
                      {call.recording_s3_key && (
                        <Volume2 className="w-3.5 h-3.5 text-green-500" title="Recording available" />
                      )}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        {formatDuration(call.duration_seconds)}
                      </div>
                      <Badge variant={getStatusVariant(call.status)} small>
                        {call.status}
                      </Badge>
                      {expandedCall === call.id
                        ? <ChevronUp className="w-4 h-4 text-gray-400" />
                        : <ChevronDown className="w-4 h-4 text-gray-400" />
                      }
                    </div>
                  </div>

                  {expandedCall === call.id && (
                    <div className="px-3 pb-3 bg-gray-50 border-t border-gray-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                        <div>
                          <span className="text-gray-500">Status</span>
                          <p className="text-gray-900 font-medium">{call.status}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Duration</span>
                          <p className="text-gray-900 font-medium">{formatDuration(call.duration_seconds)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">From</span>
                          <p className="text-gray-900">{call.from_number}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Hangup Cause</span>
                          <p className="text-gray-900">{call.hangup_cause || '-'}</p>
                        </div>
                      </div>

                      {call.recording_s3_key && recordingUrls[call.id] && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                            <Volume2 className="w-3 h-3" /> Recording
                          </p>
                          <audio controls className="w-full" style={{ height: '32px' }}>
                            <source src={recordingUrls[call.id]} type="audio/mpeg" />
                          </audio>
                        </div>
                      )}

                      {call.transcription && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Transcript
                          </p>
                          {call.transcript_data ? (
                            <TranscriptDisplay transcript={call.transcript_data} />
                          ) : (
                            <div className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-700 whitespace-pre-wrap max-h-60 overflow-y-auto">
                              {call.transcription}
                            </div>
                          )}
                        </div>
                      )}

                      {call.ai_summary && (
                        <div className="mt-2 bg-blue-50 border border-blue-100 rounded p-2">
                          <p className="text-xs text-blue-800">{call.ai_summary}</p>
                        </div>
                      )}

                      {call.transcription_status === 'processing' && (
                        <p className="mt-2 text-xs text-amber-600">Transcription in progress...</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Panel>

        {totalPages > 1 && (
          <div className="mt-2 flex items-center justify-between">
            <p className="text-xs text-gray-700">
              Page {currentPage} of {totalPages} ({totalCount} total)
            </p>
            <div className="flex gap-1">
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                variant={currentPage === 1 ? 'secondary' : 'primary'}
                size="sm"
              >
                Previous
              </Button>
              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                variant={currentPage === totalPages ? 'secondary' : 'primary'}
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CallsPage;
